/**
 * Anchor-scroll for mobile keyboards (docs/OnboardingRevamp.md §3 N1 keyboard
 * rule 1 / §3b G6): focusing a text input must bring the field AND the
 * controls below it — through the primary CTA — above the keyboard in one
 * motion, with no dead space and no manual scrolling.
 *
 * Mechanism (the spec's suggested one): `scrollIntoView({ block: 'center' })`
 * on focus, delayed so the keyboard finishes opening and the visual viewport
 * settles before we measure. Centering the focused field leaves the lower
 * half of the (shrunken) viewport for the fields below it. Combine with a
 * `scroll-mb-*` utility on the input for extra clearance where needed.
 */
import { useEffect, useLayoutEffect } from 'react';

/**
 * Keep sticky chrome (the site header) visible while an on-screen keyboard is
 * open — iOS only, no-op everywhere else.
 *
 * THE MECHANISM (three wrong guesses preceded this one, so it's worth writing
 * down): when iOS opens the keyboard it shrinks the VISUAL viewport and pans it
 * down inside the unchanged LAYOUT viewport. `position: sticky`/`fixed` pin to
 * the LAYOUT viewport, so the header ends up above the visible area — chopped.
 * The pan is NOT a document scroll: `window.scrollY` may not change at all, so
 * `scrollTo(0, 0)` cannot fix it, and `overflow: hidden` cannot prevent it
 * (worse: it removes the scrolling ancestor and breaks sticky outright).
 *
 * The only reliable correction is to move the element back down by exactly the
 * pan distance, `visualViewport.offsetTop`, and to keep doing so as the value
 * changes. Applied to the header element itself, so EVERY screen benefits —
 * long scrollable forms included — and nothing about page scrolling changes.
 */
export function useVisualViewportOffset(ref: { current: HTMLElement | null }): void {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let applied = -1;
    const apply = () => {
      const el = ref.current;
      if (!el) return;
      const offset = Math.round(vv.offsetTop);
      if (offset === applied) return;
      applied = offset;
      // Sub-pixel jitter during the keyboard animation would look shaky.
      el.style.transform = offset > 1 ? `translateY(${offset}px)` : '';
    };
    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    // MEASURED ON-DEVICE (iOS Chrome, 'hdr124' while offsetTop was already 0):
    // the events that signal the END of a pan are not reliably delivered, so an
    // event-only implementation left the header translated down permanently.
    // Poll as the source of truth; the early-return makes it a no-op read.
    const poll = window.setInterval(apply, 200);
    return () => {
      window.clearInterval(poll);
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      const el = ref.current;
      if (el) el.style.transform = '';
    };
  }, [ref]);
}

/**
 * Scroll-to-top on IN-PLACE stage swaps (§3b G6). OnboardingShell resets the
 * scroll when the ROUTE'S step changes, but screens that reveal a new stage
 * on the same route (Konto details↔SMS code, Miasto city↔districts, the auth
 * chooser→email→code steps) keep the previous stage's offset — after typing,
 * the next stage opened mid-scroll with its title cut off. Call with the
 * stage value; every change snaps back to the top (rAF catches iOS restoring
 * its remembered offset after the first call).
 */
export function useScrollTopOnChange(stage: unknown): void {
  useLayoutEffect(() => {
    // Inside the funnel the document is locked and scrolling happens in the
    // shell's scroller, so resetting `window` would be a no-op there.
    const reset = () => {
      const scroller = document.querySelector('[data-funnel-scroll]');
      if (scroller) scroller.scrollTop = 0;
      else window.scrollTo(0, 0);
    };
    reset();
    const raf = requestAnimationFrame(reset);
    return () => cancelAnimationFrame(raf);
  }, [stage]);
}

export function anchorScrollOnFocus(event: { currentTarget: HTMLElement }): void {
  const el = event.currentTarget;
  window.setTimeout(() => {
    // The element may have unmounted (stage switch) before the timer fires.
    if (!el.isConnected) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    // 400ms: after the keyboard animation AND KeyboardRoomSpacer's on-demand
    // grant (visualViewport resize / 200ms poll) — centering any earlier runs
    // before the page has the scroll room to actually lift the field.
  }, 400);
}
